import DealIcon from "../../../../../assets/svg/deal-icon";
import InfoBtnIcon from "../../../../../assets/svg/info-btn-icon";
import AiSummaryIcon from "../../../../../assets/svg/ai-summary-icon";
import GalleryIcon from "../../../../../assets/svg/gallery-icon";
import NoteIcon from "../../../../../assets/svg/note-icon";
import VerbachatIcon from "../../../../../assets/svg/verbachat-icon";
import VerbamessengerIcon from "../../../../../assets/svg/verbamessenger-icon";
import VerbamailIcon from "../../../../../assets/svg/verbamail-icon";
import VerbacallIcon from "../../../../../assets/svg/verbacall-icon";
import type {
  DealStageConfig,
  DealPipelineOption,
  DealViewTab,
  DealMetricConfig,
  DealViewItem,
  DealFilterConfig,
  DealDetailsSidebarItem,
} from "./types";

// --- UI Labels (single source for deal/booking terminology) ---

export const DEAL_LABELS = {
  singular: "Booking",
  plural: "Bookings",
  singularLower: "booking",
  pluralLower: "bookings",

  allItems: "All Bookings",
  myItems: "My Bookings",
  name: "Booking Name",
  owner: "Booking Owner",
  amount: "Booking Amount",
  stage: "Booking Stage",
  details: "Booking Details",
  assignOwnerTitle: "ASSIGN BOOKING OWNER",
  assignOwnerSubHeading: "Assign an owner to this booking.",
  addOwner: "Add Booking Owner",
  aboutThis: "About this booking",
  notFound: "Booking not found",
  searchPlaceholder: "Search by booking name, property",
  noSearchResults: "No bookings found",
  inlineSearchPlaceholder: "Search bookings",
  createdDateRange: "Booking Created Date Range",
  amountRange: "Booking Amount Range",
  highValue: "High Value Bookings",

  totalAmount: "Total booking amount",
  weightedAmount: "Weighted booking amount",
  openAmount: "Open booking amount",
  closedAmount: "Closed booking amount",
  newAmount: "New booking amount",
  averageAge: "Average booking age",
  averagePerItem: "Average per booking",
} as const;

// --- Defaults ---

export const DEFAULT_DEALS_PER_PAGE = 10;
export const DEFAULT_PIPELINE_KEY = "student_lettings";
export const DEFAULT_CURRENCY = "GBP";
export const WEIGHTED_AMOUNT_PERCENTAGE = 0.4;

// --- Stage Configs (fallback, matches board API shape without deals) ---

export const DEAL_STAGE_CONFIGS: DealStageConfig[] = [
  {
    stage_key: "draft",
    stage_title: "Draft",
    primary_hex_code: "#6674880D",
    secondary_hex_code: "#1396aacc",
    order: 1,
  },
  {
    stage_key: "contract_sent",
    stage_title: "Contract Sent",
    primary_hex_code: "#F768081A",
    secondary_hex_code: "#f76808cc",
    order: 2,
  },
  {
    stage_key: "ready_to_countersign",
    stage_title: "Ready to Countersign",
    primary_hex_code: "#1C6CFF0D",
    secondary_hex_code: "#1276e2cc",
    order: 3,
  },
  {
    stage_key: "complete",
    stage_title: "Complete",
    primary_hex_code: "#1C6CFF0D",
    secondary_hex_code: "#00a32acc",
    order: 4,
  },
  {
    stage_key: "cancelled",
    stage_title: "Cancelled",
    primary_hex_code: "#E13F411A",
    secondary_hex_code: "#e13f41cc",
    order: 5,
  },
  {
    stage_key: "expired",
    stage_title: "Expired",
    primary_hex_code: "#7986990D",
    secondary_hex_code: "#798699",
    order: 6,
  },
];

// --- Pipelines ---

export const DEAL_PIPELINES: DealPipelineOption[] = [
  { value: "concurrent_pipeline", content: "Concurrent pipeline" },
];

// --- View Tabs ---

export const DEAL_VIEW_TABS: DealViewTab[] = [
  { id: "all_deals", label: DEAL_LABELS.allItems, isDefault: true },
  { id: "my_deals", label: DEAL_LABELS.myItems, isDefault: false },
  { id: "here_ay2425", label: "Here AY2425", isDefault: false },
  { id: "ay2425_in_room", label: "AY2425 In Room", isDefault: false },
  { id: "now_ay2425", label: "Now AY2425", isDefault: false },
  { id: "export", label: "Export", isDefault: false },
];

// --- Saved Views ---

export const DEAL_SAVED_VIEWS: DealViewItem[] = [
  { id: "high_value", name: DEAL_LABELS.highValue, emoji_icon: "\u{1F4B0}" },
  { id: "closing_soon", name: "Closing Soon", emoji_icon: "\u23F0" },
  { id: "new_this_month", name: "New This Month", emoji_icon: "\u{1F195}" },
];

// --- Metrics ---

export const DEAL_METRICS: DealMetricConfig[] = [
  {
    title: DEAL_LABELS.totalAmount,
    amount: "\u00A3423.63M",
    subtitle: DEAL_LABELS.averagePerItem,
    subtitleValue: "\u00A38.7K",
  },
  {
    title: DEAL_LABELS.weightedAmount,
    amount: "\u00A3238.53M",
    subtitle: DEAL_LABELS.averagePerItem,
    subtitleValue: "\u00A34.9K",
  },
  {
    title: DEAL_LABELS.openAmount,
    amount: "\u00A36.75M",
    subtitle: DEAL_LABELS.averagePerItem,
    subtitleValue: "\u00A38.88K",
  },
  {
    title: DEAL_LABELS.closedAmount,
    amount: "\u00A3235.1M",
    subtitle: DEAL_LABELS.averagePerItem,
    subtitleValue: "\u00A34.18K",
  },
  {
    title: DEAL_LABELS.newAmount,
    amount: "\u00A32.28M",
    subtitle: DEAL_LABELS.averagePerItem,
    subtitleValue: "\u00A38.95K",
  },
  {
    title: DEAL_LABELS.averageAge,
    amount: "11.5 days",
  },
];

// --- Filter Fields (PRD: 11 view filter fields) ---

export const DEAL_FILTER_FIELDS: DealFilterConfig[] = [
  { field: "deal_owner", label: DEAL_LABELS.owner, type: "multi_select" },
  { field: "created_at", label: "Created At", type: "date_range" },
  { field: "updated_at", label: "Updated At", type: "date_range" },
  { field: "contract_start", label: "Contract Start Date", type: "date_range" },
  { field: "contract_end", label: "Contract End Date", type: "date_range" },
  { field: "deal_amount", label: DEAL_LABELS.amount, type: "number_range" },
  { field: "room_type", label: "Room Type", type: "multi_select" },
  { field: "property", label: "Property", type: "multi_select" },
  { field: "city", label: "City", type: "multi_select" },
  { field: "deal_stage", label: DEAL_LABELS.stage, type: "multi_select" },
  { field: "availability_title", label: "Availability Title", type: "text" },
];

// --- Deal Details Page: Right Sidebar Tabs ---

export const DEAL_DETAILS_RIGHT_PANEL_SIDEBAR_ITEMS: DealDetailsSidebarItem[] =
  [
    { id: "deal-details", label: DEAL_LABELS.details, icon: DealIcon },
    { id: "site-info", label: "Site Info", icon: InfoBtnIcon },
    // { id: "ai-summary", label: "AI Summary", icon: AiSummaryIcon },
    // { id: "attachments", label: "Attachments", icon: GalleryIcon },
    { id: "activity-logs", label: "Activity Logs", icon: NoteIcon },
    // { id: "deals", label: "Deals", icon: DealIcon },
  ];

// --- Deal Details Page: "About this deal" Fields ---

export const ABOUT_DEAL_FIELDS = [
  { key: "assignee", label: DEAL_LABELS.owner },
  { key: "updated_at", label: "Last Modified Date", type: "datetime" },
  // { key: "priority", label: "Priority", type: "priority" },
  // { key: "cancellation_reason", label: "Cancellation Reason" },
  // { key: "cancellation_reason_comments", label: "Cancellation Reason Comments" },
  { key: "contract_id", label: "Contract id" },
  { key: "signature_id", label: "Signature id" },
  { key: "stage", label: "Contract State", type: "stage" },
  { key: "brand", label: "Brand" },
  { key: "weekly_rent", label: "Contract Weekly Rent", type: "currency" },
  { key: "contract_weeks", label: "Contract Weeks" },
  { key: "tenant_signed", label: "Tenant Signed", type: "boolean" },
  { key: "guarantor_signed", label: "Guarantor Signed", type: "boolean" },
  // { key: "promo_code", label: "Promo Code" },
  // { key: "reporting_code", label: "Reporting Code" },
  { key: "availability_title", label: "Availability Title" },
  { key: "property_name", label: "Property" },
  { key: "contract_start", label: "Contract Start", type: "date" },
  { key: "contract_end", label: "Contract End", type: "date" },
  { key: "city", label: "City" },
] as const;

// --- Deal Details Page: Shared Constants ---

export const PROGRESS_BAR_GRADIENT =
  "linear-gradient(90deg, #c6bbff 0%, #927cff 50%, #5574ff 100%)";

export const PRIORITY_DOT_COLOR = "#f5a623";

export const MODALITY_ICONS: Record<
  string,
  React.FC<{ className?: string }>
> = {
  verbachat: VerbachatIcon,
  whatsapp: VerbamessengerIcon,
  email: VerbamailIcon,
  call: VerbacallIcon,
};

// --- Routes ---

export const CRM_LEAD_DETAIL_ROUTE = "/dashboard/crm";
